import { authClient } from './client'
import type { User, Role, Path, AuthAuditLog, Business } from '../types'

// ── Auth ──────────────────────────────────────────────────────────────────────
export const login = (email: string, password: string) =>
  authClient.post<{ accessToken: string; refreshToken: string; user: { id: string; businessId: string; email: string; fullName: string; roles: string[]; enabledModules: string[] } }>('/auth/login', { email, password })

export const register = (data: { title: string; email: string; registrationId: string; fullName: string; phone: string }) =>
  authClient.post('/auth/register', data)

// Phase 6 — self-service signup into an existing business (e.g. EnergyOS homeowners). Distinct
// from register(), which always creates a brand-new business. No roleName field: the role
// granted on verification is fixed server-side.
export const signupEnergyOsUser = (data: { businessId: string; fullName: string; email: string; phone: string; password: string }) =>
  authClient.post<{ message: string; userId: string }>('/auth/signup', data)

export const verifyEmail = (token: string) =>
  authClient.get<{ message: string; userId: string }>(`/auth/verify/${token}`)

// The caller's own business — ownership is derived server-side from the bearer token.
export const getMyBusiness = () =>
  authClient.get<Business>('/auth/me/business')

// ── Users ─────────────────────────────────────────────────────────────────────
export const getUsers = (businessId: string) =>
  authClient.get<User[]>('/auth/users', { params: { businessId } })

export const createUser = (data: { businessId: string; fullName: string; email: string; phone: string }) =>
  authClient.post<User>('/auth/users', data)

// Always changes the caller's own password — the backend derives the user from the caller's
// own bearer token, not a client-supplied id.
export const changePassword = (currentPassword: string, newPassword: string) =>
  authClient.post('/auth/change-password', { currentPassword, newPassword })

// ── Roles ─────────────────────────────────────────────────────────────────────
export const getRoles = (businessId: string) =>
  authClient.get<Role[]>('/auth/roles', { params: { businessId } })

export const createRole = (data: { businessId: string; name: string; description?: string }) =>
  authClient.post<Role>('/auth/roles', data)

export const assignRole = (userId: string, roleId: string) =>
  authClient.post('/auth/roles/assign', { userId, roleId })

export const revokeRole = (userId: string, roleId: string) =>
  authClient.delete('/auth/roles/revoke', { data: { userId, roleId } })

// ── Paths ─────────────────────────────────────────────────────────────────────
export const getPaths = (businessId: string) =>
  authClient.get<Path[]>('/auth/paths', { params: { businessId } })

export const createPath = (data: { businessId: string; method: string; url: string; description?: string }) =>
  authClient.post<Path>('/auth/paths', data)

export const getPathsByRole = (roleId: string) =>
  authClient.get<Path[]>(`/auth/paths/role/${roleId}`)

export const assignPath = (roleId: string, pathId: string) =>
  authClient.post('/auth/paths/assign', { roleId, pathId })

export const revokePathFromRole = (roleId: string, pathId: string) =>
  authClient.delete('/auth/paths/revoke', { data: { roleId, pathId } })

export const updatePath = (id: string, data: { method?: string; url?: string; description?: string }) =>
  authClient.put<Path>(`/auth/paths/${id}`, data)

export const deletePath = (id: string) =>
  authClient.delete(`/auth/paths/${id}`)

// ── Audit Logs ────────────────────────────────────────────────────────────────
export const getAuthAuditLogs = (params: { businessId?: string; userId?: string; action?: string; limit?: number }) =>
  authClient.get<AuthAuditLog[]>('/auth/audit-logs', { params })
